import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TrendingUp, Target, Calculator, Award, BarChart, Zap } from 'lucide-react';

const MaximizingWinningsGuide = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Maximizing Your Winnings
            </h1>
            <p className="text-xl text-book-brown-dark leading-relaxed">
              Advanced techniques for optimizing your gameplay and turning entertainment into profitable opportunities.
            </p>
          </div>

          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Guide Writing Philosophy</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p>Every guide published on Brit Ember represents extensive research and deep industry insights...</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-6">Strategic Approaches to Casino Success</h2>
            <p>Maximizing winnings represents the ultimate goal for serious casino players...</p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Winning Strategy Analytics</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Strategy Effectiveness</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Bankroll Management</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '95%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">95%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Game Selection</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '88%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">88%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Bonus Optimization</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-yellow-500 h-3 rounded-full" style={{width: '82%'}}></div>
                      </div>
                      <span className="font-semibold text-yellow-600">82%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Timing Strategies</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-yellow-500 h-3 rounded-full" style={{width: '75%'}}></div>
                      </div>
                      <span className="font-semibold text-yellow-600">75%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Win Rate by Experience Level</h3>
                <div className="flex justify-center mb-4">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#FFD700" strokeWidth="20"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#228B22" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="31.42"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#8B4513" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="62.83"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#4169E1" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="87.96"/>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-book-brown">100%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center"><div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>Expert Players</span>
                    <span className="font-semibold">35%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center"><div className="w-3 h-3 bg-green-600 rounded-full mr-2"></div>Advanced Players</span>
                    <span className="font-semibold">45%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center"><div className="w-3 h-3 bg-book-brown rounded-full mr-2"></div>Intermediate</span>
                    <span className="font-semibold">15%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center"><div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>Beginners</span>
                    <span className="font-semibold">5%</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Profitability Factors</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Discipline</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '100%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">Critical</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Game Knowledge</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '95%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">Essential</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Bankroll Size</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-yellow-500 h-3 rounded-full" style={{width: '85%'}}></div>
                      </div>
                      <span className="font-semibold text-yellow-600">Important</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Luck Factor</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-red-500 h-3 rounded-full" style={{width: '70%'}}></div>
                      </div>
                      <span className="font-semibold text-red-600">Variable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
            <p>Maximizing winnings represents the ultimate goal for serious casino players...</p>
            <div className="bg-book-beige rounded-lg p-6 my-8">
              <h4 className="text-xl font-bold text-book-brown mb-4">Essential Winning Maximization Principles</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Calculator className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Focus on games with favorable mathematical structures and optimal strategies</span>
                </li>
                <li className="flex items-start">
                  <Target className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Set realistic goals and maintain discipline in all gaming decisions</span>
                </li>
                <li className="flex items-start">
                  <BarChart className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Track performance and continuously refine strategies based on results</span>
                </li>
                <li className="flex items-start">
                  <Award className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Balance short-term wins with long-term profitability and sustainability</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MaximizingWinningsGuide;
